import React, { Fragment, useState } from 'react'
import { TeacherQuestionProps } from '../Question'
import TeacherHeader from '../../components/teacher-header/TeacherHeader'
import {
	QuestionChoice,
	MediaObj,
	validate,
} from '@mission.io/question-toolkit'
import UploadImage from '../../components/upload-image/UploadImage'
import {
	getChoiceHandlers,
	isMultipleSelect,
	isMultipleChoice,
	isMultipleChoiceAnswer,
	isMultipleSelectAnswer,
	QuestionOptionIcon,
	getQuestionOptionIndex,
	Image,
} from '../../utils'
import Popover from '../../components/popover'
import ChoicesResponse from '../../components/choices-response/ChoicesResponse'
import ImageModal from '../../components/image-modal/ImageModal'
import { TextImageField } from '../../components/text-image-field/TextImageField'
import EditFooter from '../../components/edit-footer'
import Collapsible from '../../components/collapsible'
import AnalyticsSingleStudentContainer from '../../components/analytics-single-student/AnalyticsSingleStudent'
import { HeaderContent } from '../image-labeling/Teacher'
import { FaArrowLeft, FaTrash } from 'react-icons/fa'
import { Thumbnail } from '../../assets/img'
import {
	InputGroup,
	AddButton,
	ReadOnly,
	ReadOnlyFormInput,
	QuestionOption,
	ImageViewer,
	CheckboxButton,
	ReadOnlyChoiceAnswerMarker,
} from '../../assets/styles'
import 'styled-components/macro'
import { styled } from 'styled-components'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

/**
 * A component for a teacher View where can create the multiple choice question. It also has a readOnly view
 * where it can't be edited
 * @returns {JSX.Element}
 */
export default function TeacherMultipleChoice(
	props: TeacherQuestionProps
): JSX.Element {
	const {
		question,
		onChange,
		questionIndex,
		readOnly,
		studentResponse,
		isQuestionAnalytics,
		studentName,
		onRequestClose,
		isCollapsibleQuestion,
		buttonAction,
		autoFocus = false,
	} = props

	const [openModalIndex, setOpenModalIndex] = useState<number | null>(null)

	if (!question || !(isMultipleSelect(question) || isMultipleChoice(question)))
		return <Fragment />

	const id =
		question._id || 'new-question-' + String(questionIndex ?? 'unknownIndex')

	const {
		handleDeleteChoice,
		handleDeleteImageChoice,
		handleAddChoice,
		handleAnswerChange,
		handleChange,
		handleChangeChoiceImage,
	} = getChoiceHandlers(onChange, readOnly)

	const openModalChoice =
		openModalIndex !== null ? question.choices[openModalIndex] : null

	const mediaForOpenModalChoice = openModalChoice?.media?.find(
		mediaItem => mediaItem.type === 'IMAGE'
	)
	const mediaUrlForOpenModalChoice = mediaForOpenModalChoice?.url || ''

	const isValid = !validate(question)?.error

	if (isQuestionAnalytics) {
		const studentAnswer = studentResponse?.answer
		const studentChoiceSelected =
			studentAnswer &&
			(isMultipleChoiceAnswer(studentAnswer) ||
				isMultipleSelectAnswer(studentAnswer))
				? studentAnswer.value
				: ''
		return onRequestClose ? (
			<AnalyticsSingleStudentContainer
				title={
					isMultipleChoice(question)
						? 'Multiple Choice Question'
						: 'Multiselect Question'
				}
				studentResponse={studentResponse}
				studentName={studentName}
				onRequestClose={onRequestClose}
				question={question}>
				<ChoicesResponse
					choices={question.choices}
					studentChoiceSelected={studentChoiceSelected}
					iconPrepend={isMultipleSelect(question)}
				/>
			</AnalyticsSingleStudentContainer>
		) : (
			<Fragment />
		)
	}

	const [expand, setExpand] = useState(false)
	const choicesHasText = Boolean(
		question?.choices.find((choice: QuestionChoice) => choice?.text !== '')
	)

	const imageIcon = (choice: QuestionChoice, idx: number) => {
		return (
			<Popover
				arrowColor="white"
				trigger={
					<img
						alt={`Icon for choice ${idx + 1}`}
						src={Thumbnail}
						css={`
							width: ${_tempTheme.spacing4};
							height: ${_tempTheme.spacing4};
							margin: 0 ${_tempTheme.spacing2};
							transform: translateY(1px);
							cursor: pointer;
							display: flex;
						`}
					/>
				}>
				<ImageViewer
					alt={`Media for choice ${idx + 1}`}
					// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					src={choice.media?.[0].url}
				/>
			</Popover>
		)
	}

	if (isCollapsibleQuestion)
		return (
			<div>
				<Collapsible
					handleClick={() => setExpand(state => !state)}
					expand={expand}
					header={
						<HeaderContent>
							<InlineDiv>
								<InlineDiv className="notranslate">Q:&nbsp;</InlineDiv>
								<InlineDiv>
									<OptionalMarkdown className="prose-invert">
										{question.phrase}
									</OptionalMarkdown>
								</InlineDiv>
							</InlineDiv>
							<InlineDiv>
								<Image media={question.media} expandable />
							</InlineDiv>
						</HeaderContent>
					}
					buttonAction={buttonAction}>
					<div>
						{question?.choices.map((choice: QuestionChoice, idx: number) => {
							const doesChoiceHaveImg = choice.media?.[0]?.url

							return (
								<QuestionOption
									key={String(choice._id) || `${idx}-question-option`}
									$isReadOnly={true}
									$chosen={false}
									$correct={choice.answer}>
									<QuestionOptionIcon
										studentChoice={false}
										correctOption={choice.answer}
									/>
									<div css="text-align: left;">
										<InlineDiv>
											{getQuestionOptionIndex(idx) + ':'}&nbsp;
										</InlineDiv>
										<InlineDiv>
											{choicesHasText ? (
												choice.text ? (
													<OptionalMarkdown className="prose-invert">
														{choice.text}
													</OptionalMarkdown>
												) : (
													'(No text provided)'
												)
											) : null}
										</InlineDiv>
									</div>
									{doesChoiceHaveImg && imageIcon(choice, idx)}
								</QuestionOption>
							)
						})}
					</div>
				</Collapsible>
			</div>
		)

	return readOnly ? (
		<ReadOnly className="Question">
			<TeacherHeader
				darkMode
				questionIndex={questionIndex}
				isValid={isValid}
				question={question}
				studentResponse={studentResponse}
				readOnly={readOnly}
			/>
			{question.choices.map((choice: QuestionChoice, i: number) => {
				const doesChoiceHaveImg = !!choice?.media?.find(
					mediaItem => mediaItem.type === 'IMAGE'
				)?.type
				return (
					<ReadOnlyFormInput $correct={Boolean(choice.answer)} key={`id-${i}`}>
						<ReadOnlyChoiceAnswerMarker
							shape={isMultipleChoice(question) ? 'circle' : 'square'}
							checkmark={choice.answer}
						/>

						<div className="question-choice">
							<OptionalMarkdown className="prose-invert">
								{choice.text}
							</OptionalMarkdown>
						</div>
						{studentResponse &&
							studentResponse.answer.value === String(choice._id) && (
								<div className="question-choice">
									<FaArrowLeft />
									{"Student's answer"}
								</div>
							)}
						{doesChoiceHaveImg && imageIcon(choice, i)}
					</ReadOnlyFormInput>
				)
			})}
		</ReadOnly>
	) : (
		<div>
			<TeacherHeader
				label={
					isMultipleChoice(question) ? 'Multiple Choice' : 'Multiple Select'
				}
				readOnly={readOnly}
				question={question}
				questionIndex={questionIndex}
				isValid={isValid}
				onChange={onChange}
				autoFocus={autoFocus}
				studentResponse={studentResponse}
			/>
			<div className="bg-brand-cloud-blue text-brand-space-blue p-4">
				{question.choices.map((choice: QuestionChoice, i: number) => {
					const val = id + '-' + i
					const doesChoiceHaveImg = !!choice?.media?.find(
						(mediaItem: MediaObj) => mediaItem.type === 'IMAGE'
					)?.type

					return (
						<InputGroup key={val} role="listitem">
							<CheckboxButton
								aria-label="Mark Correct"
								onClick={() => handleAnswerChange(i)}
								selected={choice.answer}
							/>
							<TextImageField
								type="text"
								placeholder="Type Answer..."
								value={choice.text}
								onChange={(e: any) => handleChange(i, e.currentTarget.value)}
								media={choice.media}
								onDeleteImage={() => handleDeleteImageChoice(i)}
								handleAddChoice={
									i + 1 === question.choices.length
										? handleAddChoice
										: undefined
								}
								autoFocus={false}
							/>
							<div css="& > * {display: inline-block;} ">
								<UploadImage
									disabled={doesChoiceHaveImg}
									onChange={handleChangeChoiceImage}
									index={i}
									readOnly={readOnly}
								/>

								<DeleteChoice
									aria-label="Delete Choice"
									onClick={() => handleDeleteChoice(i)}>
									<FaTrash size={18} />
								</DeleteChoice>
							</div>
						</InputGroup>
					)
				})}
				<AddButton onClick={handleAddChoice}>Add Another Answer</AddButton>
			</div>
			{typeof openModalIndex === 'number' && openModalChoice && (
				<ImageModal
					isOpen
					url={mediaUrlForOpenModalChoice}
					infoText={`Answer ${openModalIndex + 1} ${
						openModalChoice.answer ? '(Correct)' : ''
					}`}
					text={openModalChoice.text}
					setClose={() => setOpenModalIndex(null)}
				/>
			)}
			<EditFooter onChange={onChange} />
		</div>
	)
}

const InlineDiv = styled.div`
	display: inline-block;
`

export const DeleteChoice = styled.button`
	color: ${({ theme }) => theme.primary};
	cursor: pointer;
	border: none;
	background: transparent;
	padding: 0;
	height: 18px;
	width: 18px;
	margin: ${_tempTheme.spacing2};
	svg {
		margin: 0;
	}
`
